import type { RouteObject } from "react-router-dom";
import { Spinner } from "@/components/ui/v2/spinner";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import {
  CompanyAuditLogsPage,
  CompanyAuditLogsListPage,
  CompanyAuditLogSettingsPage,
  CompanyAuditLogDetailPage,
} from "@/utils/lazyLoadComponents";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_AUDIT_LOGS_DYNAMIC_ID_KEY = "id";

export enum COMPANY_AUDIT_LOGS_PATH_SEGMENTS {
  ROOT = "audit-logs",
  SETTINGS = "settings",
  DETAILS = `:${COMPANY_AUDIT_LOGS_DYNAMIC_ID_KEY}`,
}

// ─── Route Definition ─────────────────────────────────────────────────────────
// New top-level module, not a Settings tab — an "Audit Logs" permission must be
// grantable independently of Settings (see AUDIT_LOGS_PLAN.md finding A).
export const companyAuditLogsRoutes: RouteObject = {
  path: COMPANY_AUDIT_LOGS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Audit Logs" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout (single "Audit Logs" tab at the module root,
    // matching CompanyTeamsPage's shape) ────────────────────────────────────
    {
      Component: CompanyAuditLogsPage,
      handle: { flushContent: true },
      children: [{ index: true, Component: CompanyAuditLogsListPage }],
    },

    // ── Module settings — a SIBLING of the tabs layout, never a child of it
    // (see routes.teams.ts for the same reasoning: nesting it inside would
    // render it under a tab bar with no matching tab). ─────────────────────
    {
      path: COMPANY_AUDIT_LOGS_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Audit Log Settings" },
      Component: CompanyAuditLogSettingsPage,
    },

    // ── Single-entry detail page — also a SIBLING of the tabs layout, same
    // reasoning as SETTINGS above (see routes.announcement.ts DETAILS for
    // the identical shape on a non-workflow module). ────────────────────────
    {
      path: COMPANY_AUDIT_LOGS_PATH_SEGMENTS.DETAILS,
      handle: { breadcrumb: "Details", title: "Audit Log Entry" },
      Component: CompanyAuditLogDetailPage,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_AUDIT_LOGS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_AUDIT_LOGS_PATH_SEGMENTS.ROOT)),
  SETTINGS: withCompanyPrefix(
    addSlashes(
      COMPANY_AUDIT_LOGS_PATH_SEGMENTS.ROOT,
      COMPANY_AUDIT_LOGS_PATH_SEGMENTS.SETTINGS,
    ),
  ),
  VIEW: (id: string) =>
    withCompanyPrefix(
      addSlashes(COMPANY_AUDIT_LOGS_PATH_SEGMENTS.ROOT, id),
    ),
});
